"use client";

/**
 * Renders the activity store's undismissed items as toasts (tdd-0079 §6).
 *
 * Mounted once per app, beside `<ActivityProvider>`. Everything interesting
 * about *which* items appear and how they merge lives in the store; this is the
 * view.
 */

import * as React from "react";

import {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@workspace/ui/components/toast";
import { useActivityMaybe, type ActivityItem } from "@workspace/ui/hooks/useActivityFeed";
import { cn } from "@workspace/ui/lib/utils";

export interface ToasterProps {
  /** Called when a toast's action is used, e.g. to open the activity panel. */
  onAction?: (item: ActivityItem) => void;
  className?: string;
}

export function Toaster({ onAction, className }: ToasterProps) {
  const activity = useActivityMaybe();

  // Render nothing outside a provider rather than throwing. This may sit in a
  // shared layout that some routes mount without activity, and a layout-level
  // throw takes the whole page.
  if (!activity) return null;

  return (
    <ToastProvider swipeDirection="right">
      {activity.toasts.map((item) => (
        <Toast
          key={item.id}
          level={item.level}
          duration={Infinity}
          // Dismissal is owned by the store — it holds the timers, so that a
          // merge can restart the clock rather than letting the original expiry
          // fire on refreshed content. Radix would otherwise run a second,
          // competing timer.
          onOpenChange={(open) => {
            if (!open) activity.dismiss(item.id);
          }}
          // `urgent` interrupts a screen reader mid-sentence. Everything else
          // waits for a pause, which is the right trade for a save
          // confirmation.
          type={item.level === "urgent" ? "foreground" : "background"}
          className={className}
        >
          <div className="min-w-0 flex-1">
            <ToastTitle>
              {item.title}
              {item.count > 1 && (
                <span className="ml-1.5 text-muted-foreground">
                  {/* The merge count. A list of every mention would be neither
                      more actionable nor bounded — the next move is "go and
                      look" either way. */}
                  +{item.count - 1} more
                </span>
              )}
            </ToastTitle>
            {item.body && <ToastDescription>{item.body}</ToastDescription>}
          </div>

          {(item.href || onAction) && (
            <ToastAction
              altText={item.count > 1 ? "View all" : "View"}
              onClick={() => onAction?.(item)}
              // A merged item points at the feed rather than the newest
              // event: with several behind it, the list is the useful
              // destination.
              asChild={Boolean(item.href) && item.count === 1}
            >
              {item.href && item.count === 1 ? (
                <a href={item.href}>View</a>
              ) : (
                <span>{item.count > 1 ? "View all" : "View"}</span>
              )}
            </ToastAction>
          )}

          <ToastClose />
        </Toast>
      ))}
      <ToastViewport
        className={cn(
          // Sticky items must not push the viewport past the screen. Scrolling
          // is the escape valve when several accumulate.
          "max-h-[60vh] overflow-y-auto",
        )}
      />
    </ToastProvider>
  );
}
